import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Common
    search: 'Search...',
    notifications: 'Notifications',
    new: 'new',
    viewAll: 'View all',
    profile: 'Profile',
    settings: 'Settings',
    logOut: 'Log out',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    create: 'Create',
    max: 'MAX',
    done: 'Done',
    hoursAgo: 'hours ago',
    daysAgo: 'days ago',
    minAgo: 'min ago',
    
    // Dashboard
    dashboard: 'Dashboard',
    welcomeBack: 'Welcome back, Tim! Here\'s your portfolio overview.',
    portfolioValue: 'Portfolio Value',
    dailyProfitLoss: '24h Profit/Loss',
    totalTrades: 'Total Trades',
    activeAlerts: 'Active Alerts',
    updatedAgo: 'Updated 2m ago',
    thisWeek: 'this week',
    triggeredToday: 'triggered today',
    customize: 'Customize',
    editMode: 'Edit Mode',
    dragWidgets: 'Drag widgets to reorder',
    resetLayout: 'Reset Layout',
    customizeDashboard: 'Customize Your Dashboard',
    dragToReorder: 'Drag widgets to reorder • Click the eye icon to show/hide widgets',
    widgets: 'widgets',
    of: 'of',
    topBtc: 'Top: BTC +2.3%',
    
    // Sidebar
    portfolio: 'Portfolio',
    transactions: 'Transactions',
    markets: 'Markets',
    exchange: 'Exchange',
    alerts: 'Alerts',
    blockchainFlow: 'Blockchain Flow',
    designSystem: 'Design System',
    downloads: 'Downloads',
    help: 'Help',
    proTrader: 'Pro Trader',
    
    // Portfolio
    yourHoldings: 'Your Holdings',
    totalBalance: 'Total Balance',
    allAssets: 'All Assets',
    crypto: 'Crypto',
    tokens: 'Tokens',
    portfolioOverview: 'Portfolio Overview',
    managePortfolio: 'Manage and track your cryptocurrency holdings.',
    asset: 'Asset',
    holdings: 'Holdings',
    avgBuyPrice: 'Avg. Buy Price',
    currentPrice: 'Current Price',
    profitLoss: 'Profit/Loss',
    allocation: 'Allocation',
    performance: 'Performance',
    day: 'Day',
    week: 'Week',
    month: 'Month',
    year: 'Year',
    portfolioDistribution: 'Portfolio Distribution',
    recentActivity: 'Recent Activity',
    
    // Transactions
    transactionHistory: 'Transaction History',
    allTransactions: 'All Transactions',
    buy: 'Buy',
    sell: 'Sell',
    send: 'Send',
    receive: 'Receive',
    viewTransactions: 'View and manage your transaction history.',
    date: 'Date',
    type: 'Type',
    amount: 'Amount',
    status: 'Status',
    completed: 'Completed',
    pending: 'Pending',
    failed: 'Failed',
    filterByType: 'Filter by type',
    filterByStatus: 'Filter by status',
    exportCsv: 'Export CSV',
    noTransactions: 'No transactions found',
    
    // Markets
    trackPrices: 'Track cryptocurrency prices and market data in real-time.',
    totalMarketCap: 'Total Market Cap',
    volume24h: '24h Volume',
    btcDominance: 'BTC Dominance',
    fearGreed: 'Fear & Greed',
    searchCoins: 'Search coins...',
    all: 'All',
    defi: 'DeFi',
    nft: 'NFT',
    gaming: 'Gaming',
    rank: 'Rank',
    name: 'Name',
    price: 'Price',
    change24h: '24h %',
    change7d: '7d %',
    marketCap: 'Market Cap',
    volume: 'Volume (24h)',
    trending: 'Trending',
    topGainers: 'Top Gainers',
    topLosers: 'Top Losers',
    sortBy: 'Sort by',
    
    // Exchange
    swap: 'Swap',
    swapInstantly: 'Swap cryptocurrencies instantly at the best rates.',
    market: 'Market',
    limit: 'Limit',
    from: 'From',
    to: 'To',
    balance: 'Balance',
    exchangeRate: 'Exchange Rate',
    networkFee: 'Network Fee',
    yourAssets: 'Your Assets',
    recentSwaps: 'Recent Swaps',
    swapTo: 'Swap {from} to {to}',
    
    // Alerts
    priceAlerts: 'Price Alerts',
    getNotified: 'Get notified when prices hit your targets.',
    createAlert: 'Create Alert',
    totalAlerts: 'Total Alerts',
    watching: 'Watching',
    triggered: 'Triggered',
    portfolioAlerts: 'Portfolio Alerts',
    priceAbove: 'Price above',
    priceBelow: 'Price below',
    current: 'Current',
    alertHistory: 'Alert History',
    notificationSettings: 'Notification Settings',
    pushNotifications: 'Push Notifications',
    emailAlerts: 'Email Alerts',
    smsAlerts: 'SMS Alerts',
    sound: 'Sound',
    newAlert: 'New Alert',
    targetPrice: 'Target Price (USD)',
    cryptocurrency: 'Cryptocurrency',
    condition: 'Condition',
    priceGoesAbove: 'Price goes above',
    priceGoesBelow: 'Price goes below',
    dailyProfitExceeds: 'Daily profit exceeds',
    dailyLossExceeds: 'Daily loss exceeds',
    portfolioBelow: 'Portfolio below',
    active: 'active',
    
    // Settings
    manageAccount: 'Manage your account and application preferences.',
    general: 'General',
    security: 'Security',
    appearance: 'Appearance',
    apiKeys: 'API Keys',
    billing: 'Billing',
    generalSettings: 'General Settings',
    configurePreferences: 'Configure your basic application preferences.',
    language: 'Language',
    currency: 'Currency',
    timezone: 'Timezone',
    autoRefresh: 'Auto-refresh data',
    updatePrices: 'Update prices every 30 seconds',
    profileSettings: 'Profile Settings',
    updatePersonalInfo: 'Update your personal information.',
    uploadPhoto: 'Upload Photo',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    bio: 'Bio',
    saveChanges: 'Save Changes',
    securitySettings: 'Security Settings',
    manageAccountSecurity: 'Manage your account security.',
    password: 'Password',
    lastChanged: 'Last changed 30 days ago',
    change: 'Change',
    twoFactor: 'Two-Factor Authentication',
    addExtraLayer: 'Add an extra layer of security',
    enabled: 'Enabled',
    biometricLogin: 'Biometric Login',
    useFingerprintFace: 'Use fingerprint or face ID',
    activeSessions: 'Active Sessions',
    currentSession: 'Current session',
    notificationPreferences: 'Notification Preferences',
    chooseNotifications: 'Choose how you want to be notified.',
    tradeConfirmations: 'Trade Confirmations',
    receiveConfirmations: 'Receive confirmations for executed trades',
    marketNews: 'Market News',
    dailyDigest: 'Daily digest of important market news',
    portfolioUpdates: 'Portfolio Updates',
    weeklyReports: 'Weekly portfolio performance reports',
    securityAlerts: 'Security Alerts',
    accountSecurityNotifs: 'Notifications about account security',
    promotions: 'Promotions',
    specialOffers: 'Special offers and announcements',
    customizeLook: 'Customize the look and feel of the application.',
    theme: 'Theme',
    dark: 'Dark',
    light: 'Light',
    chartStyle: 'Chart Style',
    areaChart: 'Area Chart',
    lineChart: 'Line Chart',
    candlestick: 'Candlestick',
    barChart: 'Bar Chart',
    fontSize: 'Font Size',
    medium: 'Medium',
    manageApiKeys: 'Manage your API keys for third-party integrations.',
    tradingApiKey: 'Trading API Key',
    createdDaysAgo: 'Created 14 days ago',
    generateNewKey: 'Generate New API Key',
    billingSubscription: 'Billing & Subscription',
    manageSubscription: 'Manage your subscription and payment methods.',
    yourPlan: 'Your plan renews on Feb 1, 2025',
    managePlan: 'Manage Plan',
    paymentMethod: 'Payment Method',
    expires: 'Expires',
    
    // AI Insights
    aiInsights: 'AI Insights',
    live: 'Live',
    confidence: 'confidence',
    viewAllInsights: 'View All Insights',
    bitcoinBreaking: 'Bitcoin Breaking Resistance',
    bitcoinBreakingDesc: 'BTC has broken through the $67,000 resistance level with strong volume. Historical patterns suggest potential 15% upside.',
    ethOverbought: 'ETH Overbought Signal',
    ethOverboughtDesc: 'Ethereum RSI indicates overbought conditions. Consider taking partial profits or setting stop-losses.',
    marketConsolidation: 'Market Consolidation',
    marketConsolidationDesc: 'Overall crypto market is consolidating. Low volatility period expected for the next 24-48 hours.',
    bullish: 'Bullish',
    bearish: 'Bearish',
    neutral: 'Neutral',
    
    // Chart
    bitcoin: 'Bitcoin',
    priceChart: 'Price Chart',
    high24h: '24h High',
    low24h: '24h Low',
    
    // Market Table
    marketOverview: 'Market Overview',
    viewMore: 'View More',
  },
  de: {
    // Common
    search: 'Suchen...',
    notifications: 'Benachrichtigungen',
    new: 'neu',
    viewAll: 'Alle anzeigen',
    profile: 'Profil',
    settings: 'Einstellungen',
    logOut: 'Abmelden',
    save: 'Speichern',
    cancel: 'Abbrechen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    create: 'Erstellen',
    max: 'MAX',
    done: 'Fertig',
    hoursAgo: 'Stunden her',
    daysAgo: 'Tage her',
    minAgo: 'Min. her',
    
    // Dashboard
    dashboard: 'Dashboard',
    welcomeBack: 'Willkommen zurück, Tim! Hier ist deine Portfolio-Übersicht.',
    portfolioValue: 'Portfolio-Wert',
    dailyProfitLoss: '24h Gewinn/Verlust',
    totalTrades: 'Gesamte Trades',
    activeAlerts: 'Aktive Alarme',
    updatedAgo: 'Aktualisiert vor 2 Min.',
    thisWeek: 'diese Woche',
    triggeredToday: 'heute ausgelöst',
    customize: 'Anpassen',
    editMode: 'Bearbeitungsmodus',
    dragWidgets: 'Widgets ziehen zum Neuordnen',
    resetLayout: 'Layout zurücksetzen',
    customizeDashboard: 'Dashboard anpassen',
    dragToReorder: 'Widgets ziehen zum Neuordnen • Klicke auf das Auge zum Ein-/Ausblenden',
    widgets: 'Widgets',
    of: 'von',
    topBtc: 'Top: BTC +2,3%',
    
    // Sidebar
    portfolio: 'Portfolio',
    transactions: 'Transaktionen',
    markets: 'Märkte',
    exchange: 'Tausch',
    alerts: 'Alarme',
    blockchainFlow: 'Blockchain Fluss',
    designSystem: 'Design System',
    downloads: 'Downloads',
    help: 'Hilfe',
    proTrader: 'Pro Händler',
    
    // Portfolio
    yourHoldings: 'Deine Bestände',
    totalBalance: 'Gesamtguthaben',
    allAssets: 'Alle Vermögenswerte',
    crypto: 'Krypto',
    tokens: 'Token',
    portfolioOverview: 'Portfolio-Übersicht',
    managePortfolio: 'Verwalte und verfolge deine Kryptowährungsbestände.',
    asset: 'Vermögenswert',
    holdings: 'Bestände',
    avgBuyPrice: 'Durchschn. Kaufpreis',
    currentPrice: 'Aktueller Preis',
    profitLoss: 'Gewinn/Verlust',
    allocation: 'Verteilung',
    performance: 'Leistung',
    day: 'Tag',
    week: 'Woche',
    month: 'Monat',
    year: 'Jahr',
    portfolioDistribution: 'Portfolio-Verteilung',
    recentActivity: 'Letzte Aktivität',
    
    // Transactions
    transactionHistory: 'Transaktionsverlauf',
    allTransactions: 'Alle Transaktionen',
    buy: 'Kaufen',
    sell: 'Verkaufen',
    send: 'Senden',
    receive: 'Empfangen',
    viewTransactions: 'Zeige und verwalte deinen Transaktionsverlauf.',
    date: 'Datum',
    type: 'Typ',
    amount: 'Betrag',
    status: 'Status',
    completed: 'Abgeschlossen',
    pending: 'Ausstehend',
    failed: 'Fehlgeschlagen',
    filterByType: 'Nach Typ filtern',
    filterByStatus: 'Nach Status filtern',
    exportCsv: 'CSV exportieren',
    noTransactions: 'Keine Transaktionen gefunden',
    
    // Markets
    trackPrices: 'Verfolge Kryptowährungspreise und Marktdaten in Echtzeit.',
    totalMarketCap: 'Gesamte Marktkapitalisierung',
    volume24h: '24h Volumen',
    btcDominance: 'BTC-Dominanz',
    fearGreed: 'Angst & Gier',
    searchCoins: 'Coins suchen...',
    all: 'Alle',
    defi: 'DeFi',
    nft: 'NFT',
    gaming: 'Gaming',
    rank: 'Rang',
    name: 'Name',
    price: 'Preis',
    change24h: '24h %',
    change7d: '7T %',
    marketCap: 'Marktkapitalisierung',
    volume: 'Volumen (24h)',
    trending: 'Trending',
    topGainers: 'Top Gewinner',
    topLosers: 'Top Verlierer',
    sortBy: 'Sortieren nach',
    
    // Exchange
    swap: 'Tauschen',
    swapInstantly: 'Tausche Kryptowährungen sofort zu den besten Kursen.',
    market: 'Markt',
    limit: 'Limit',
    from: 'Von',
    to: 'Nach',
    balance: 'Guthaben',
    exchangeRate: 'Wechselkurs',
    networkFee: 'Netzwerkgebühr',
    yourAssets: 'Deine Vermögenswerte',
    recentSwaps: 'Letzte Tausche',
    swapTo: '{from} zu {to} tauschen',
    
    // Alerts
    priceAlerts: 'Preisalarme',
    getNotified: 'Erhalte Benachrichtigungen, wenn Preise deine Ziele erreichen.',
    createAlert: 'Alarm erstellen',
    totalAlerts: 'Gesamte Alarme',
    watching: 'Beobachten',
    triggered: 'Ausgelöst',
    portfolioAlerts: 'Portfolio-Alarme',
    priceAbove: 'Preis über',
    priceBelow: 'Preis unter',
    current: 'Aktuell',
    alertHistory: 'Alarm-Verlauf',
    notificationSettings: 'Benachrichtigungseinstellungen',
    pushNotifications: 'Push-Benachrichtigungen',
    emailAlerts: 'E-Mail-Alarme',
    smsAlerts: 'SMS-Alarme',
    sound: 'Ton',
    newAlert: 'Neuer Alarm',
    targetPrice: 'Zielpreis (USD)',
    cryptocurrency: 'Kryptowährung',
    condition: 'Bedingung',
    priceGoesAbove: 'Preis steigt über',
    priceGoesBelow: 'Preis fällt unter',
    dailyProfitExceeds: 'Tagesgewinn übersteigt',
    dailyLossExceeds: 'Tagesverlust übersteigt',
    portfolioBelow: 'Portfolio unter',
    active: 'aktiv',
    
    // Settings
    manageAccount: 'Verwalte dein Konto und Anwendungseinstellungen.',
    general: 'Allgemein',
    security: 'Sicherheit',
    appearance: 'Erscheinungsbild',
    apiKeys: 'API-Schlüssel',
    billing: 'Abrechnung',
    generalSettings: 'Allgemeine Einstellungen',
    configurePreferences: 'Konfiguriere deine grundlegenden Anwendungseinstellungen.',
    language: 'Sprache',
    currency: 'Währung',
    timezone: 'Zeitzone',
    autoRefresh: 'Auto-Aktualisierung',
    updatePrices: 'Preise alle 30 Sekunden aktualisieren',
    profileSettings: 'Profil-Einstellungen',
    updatePersonalInfo: 'Aktualisiere deine persönlichen Informationen.',
    uploadPhoto: 'Foto hochladen',
    firstName: 'Vorname',
    lastName: 'Nachname',
    email: 'E-Mail',
    bio: 'Biografie',
    saveChanges: 'Änderungen speichern',
    securitySettings: 'Sicherheitseinstellungen',
    manageAccountSecurity: 'Verwalte deine Kontosicherheit.',
    password: 'Passwort',
    lastChanged: 'Zuletzt geändert vor 30 Tagen',
    change: 'Ändern',
    twoFactor: 'Zwei-Faktor-Authentifizierung',
    addExtraLayer: 'Füge eine zusätzliche Sicherheitsebene hinzu',
    enabled: 'Aktiviert',
    biometricLogin: 'Biometrische Anmeldung',
    useFingerprintFace: 'Verwende Fingerabdruck oder Face ID',
    activeSessions: 'Aktive Sitzungen',
    currentSession: 'Aktuelle Sitzung',
    notificationPreferences: 'Benachrichtigungseinstellungen',
    chooseNotifications: 'Wähle, wie du benachrichtigt werden möchtest.',
    tradeConfirmations: 'Handelsbestätigungen',
    receiveConfirmations: 'Bestätigungen für ausgeführte Trades erhalten',
    marketNews: 'Marktnachrichten',
    dailyDigest: 'Tägliche Zusammenfassung wichtiger Marktnachrichten',
    portfolioUpdates: 'Portfolio-Updates',
    weeklyReports: 'Wöchentliche Portfolio-Leistungsberichte',
    securityAlerts: 'Sicherheitsalarme',
    accountSecurityNotifs: 'Benachrichtigungen zur Kontosicherheit',
    promotions: 'Aktionen',
    specialOffers: 'Sonderangebote und Ankündigungen',
    customizeLook: 'Passe das Aussehen der Anwendung an.',
    theme: 'Design',
    dark: 'Dunkel',
    light: 'Hell',
    chartStyle: 'Diagrammstil',
    areaChart: 'Flächendiagramm',
    lineChart: 'Liniendiagramm',
    candlestick: 'Kerzendiagramm',
    barChart: 'Balkendiagramm',
    fontSize: 'Schriftgröße',
    medium: 'Mittel',
    manageApiKeys: 'Verwalte deine API-Schlüssel für Drittanbieter-Integrationen.',
    tradingApiKey: 'Trading-API-Schlüssel',
    createdDaysAgo: 'Erstellt vor 14 Tagen',
    generateNewKey: 'Neuen API-Schlüssel generieren',
    billingSubscription: 'Abrechnung & Abonnement',
    manageSubscription: 'Verwalte dein Abonnement und Zahlungsmethoden.',
    yourPlan: 'Dein Plan verlängert sich am 1. Feb. 2025',
    managePlan: 'Plan verwalten',
    paymentMethod: 'Zahlungsmethode',
    expires: 'Läuft ab',
    
    // AI Insights
    aiInsights: 'KI-Einblicke',
    live: 'Live',
    confidence: 'Konfidenz',
    viewAllInsights: 'Alle Einblicke anzeigen',
    bitcoinBreaking: 'Bitcoin durchbricht Widerstand',
    bitcoinBreakingDesc: 'BTC hat die 67.000 $-Widerstandsmarke mit starkem Volumen durchbrochen. Historische Muster deuten auf 15% Aufwärtspotenzial hin.',
    ethOverbought: 'ETH überkauft Signal',
    ethOverboughtDesc: 'Ethereum RSI zeigt überkaufte Bedingungen. Erwäge Teilgewinne mitzunehmen oder Stop-Loss zu setzen.',
    marketConsolidation: 'Marktkonsolidierung',
    marketConsolidationDesc: 'Der gesamte Kryptomarkt konsolidiert. Niedrige Volatilität wird für die nächsten 24-48 Stunden erwartet.',
    bullish: 'Bullisch',
    bearish: 'Bärisch',
    neutral: 'Neutral',
    
    // Chart
    bitcoin: 'Bitcoin',
    priceChart: 'Preisdiagramm',
    high24h: '24h Hoch',
    low24h: '24h Tief',
    
    // Market Table
    marketOverview: 'Marktübersicht',
    viewMore: 'Mehr anzeigen',
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || translations['en'][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'de' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
